import React from "react";
import { useQuery } from "@tanstack/react-query";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";
import {
  ConflictReason,
  ConflictReasonLabels,
} from "../../models/ConflictReason";
import {
  AuditLogsRepository,
  PathCollisionReconcileFile,
} from "../../repositories/AuditLogsRepository";
import { QUERY_KEYS } from "@/constants/QueryKeys";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { dialogWide } from "@/lib/dialogSize";

// AdditionalData is serialized server-side with PascalCase keys and the MigrationType name verbatim.
const ENTITY_ROUTES: Record<string, { route: string; label: string }> = {
  CasesMigration: { route: "case", label: "Case" },
  IntakesMigration: { route: "intake", label: "Intake" },
  ProvidersMigration: { route: "provider", label: "Provider" },
  TemplatesMigration: { route: "template", label: "Templates library" },
  FirmDocumentsMigration: {
    route: "firm-documents-folder",
    label: "Firm documents folder",
  },
};

const reasonLabel = (reason: ConflictReason | null) =>
  reason ? ConflictReasonLabels[reason] : "—";

const describeChange = (file: PathCollisionReconcileFile) => {
  if (file.to === null) return `Withdrawn (was ${reasonLabel(file.from)})`;
  if (file.from === null) return `Raised: ${reasonLabel(file.to)}`;
  return `${reasonLabel(file.from)} → ${reasonLabel(file.to)}`;
};

/**
 * A client's re-check of one entity's discovery-time path-collision flags: what it withdrew because the other
 * copy is gone, relabelled because the copies' content changed, or raised because a new file now collides.
 */
export const ViewPathCollisionReconcileAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const repository = new AuditLogsRepository();

  let data: {
    Family?: string;
    MigrationEntityId?: string;
    Withdrawn?: number;
    Relabelled?: number;
    Raised?: number;
  } | null = null;
  try {
    data = auditLog?.additionalData
      ? JSON.parse(auditLog.additionalData)
      : null;
  } catch {
    data = null;
  }

  const filesQuery = useQuery({
    queryKey: [
      QUERY_KEYS.PATH_COLLISION_RECONCILE_FILES,
      organizationId,
      auditLog?.id,
    ],
    enabled: open && !!auditLog?.id,
    queryFn: () => repository.getPathCollisionReconcileFiles(auditLog!.id),
  });

  const entity = data?.Family ? ENTITY_ROUTES[data.Family] : undefined;
  const entityId = data?.MigrationEntityId ?? auditLog?.entityId;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className={`${dialogWide} overflow-y-auto`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex min-w-0 flex-col gap-2 py-2">
              {data ? (
                <>
                  {entityId && (
                    <div className="flex flex-row gap-2 text-sm">
                      <span className="font-bold">
                        {entity?.label ?? "Entity"}:
                      </span>
                      {entity ? (
                        <a
                          className="text-primary underline-offset-4 hover:underline"
                          href={`/organization/${organizationId}/${entity.route}/${entityId}`}
                        >
                          {entityId}
                        </a>
                      ) : (
                        <span>{entityId}</span>
                      )}
                    </div>
                  )}
                  <LabelValuePair
                    label="Withdrawn"
                    value={(data.Withdrawn ?? 0).toLocaleString()}
                  />
                  <LabelValuePair
                    label="Relabelled"
                    value={(data.Relabelled ?? 0).toLocaleString()}
                  />
                  <LabelValuePair
                    label="Raised"
                    value={(data.Raised ?? 0).toLocaleString()}
                  />
                </>
              ) : (
                <span className="text-sm">Failed to parse additionalData</span>
              )}

              <div className="min-w-0 overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Change</TableHead>
                      <TableHead>Source path</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filesQuery.isLoading && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-sm text-muted-foreground"
                        >
                          Loading…
                        </TableCell>
                      </TableRow>
                    )}
                    {filesQuery.isError && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="text-sm text-destructive"
                        >
                          Could not load the list of re-checked files.
                        </TableCell>
                      </TableRow>
                    )}
                    {filesQuery.data?.map((file) => (
                      <TableRow key={file.fileId}>
                        <TableCell className="text-sm break-all">
                          {file.filename ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {describeChange(file)}
                        </TableCell>
                        <TableCell className="font-mono text-xs break-all">
                          {file.filePath ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Separator />

              <LabelValuePair
                label="Performed By"
                value={auditLog.performerEmailAddress || auditLog.performedBy}
              />
              <LabelValuePair label="Event Date" value={auditLog.eventDate} />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewPathCollisionReconcileAuditLogDetailsModal;
